// Imports for external libraries go here.
import React, { FC, useRef, useState } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { ConsentsListProps } from './ConsentsList.types';
import { StyledConsentsList } from './ConsentsList.styles';
import { ConsentsCheckListModalInfo } from './ConsentsList.types';
import { toggleSelectedConsentCheckbox } from '../../../utils/consents';
import { ConsentsListItem } from './ConsentsListItem';
import { Messages, Modal } from '@marriott/mi-ui-library';
import DOMPurify from 'isomorphic-dompurify';
import clsx from 'clsx';

// Use named rather than default exports.
export const ConsentsList: FC<ConsentsListProps> = ({ consentState, onConsentChange, validateErrors }) => {
  const [consentModalInfo, setConsentModalInfo] = useState<ConsentsCheckListModalInfo>({
    showModal: false,
    modalHeader: '',
    modalBody: '',
  });
  const { showModal, modalHeader, modalBody } = consentModalInfo;
  const [errorMessage, setErrorMessage] = useState<string>(''); // State for error messages
  const activeAnchorElementBeforeModalOpen = useRef<HTMLElement | null>(null); // Track the element that opened the modal

  // Helper function to handle modal updates
  const setModalContent = (modalHeader: string, modalBody: string | Element) => {
    setConsentModalInfo({
      showModal: true,
      modalHeader,
      modalBody,
    });
    setErrorMessage(modalBody instanceof Element ? '' : modalBody); // Clear error if success
  };

  //Handling popup modal
  const onModalOpen = async (e: React.MouseEvent, hrefLink: string) => {
    e.preventDefault();

    // Store the element that triggered the modal
    activeAnchorElementBeforeModalOpen.current = e.target as HTMLElement;

    // Check if hrefLink is invalid before proceeding
    if (!hrefLink) {
      setModalContent(
        'Error: Invalid Link',
        'It appears the link you provided is invalid or not accessible. Please check the link and try again. If the issue persists, contact support for further assistance.'
      );
      return; // Exit early if there's no valid hrefLink
    }

    try {
      //const authHeader = `Basic YWRtaW46YWRtaW4=`;
      const pageResponse = await fetch(hrefLink);
      const pageHtmlForModal = await pageResponse.text();
      const parser = new DOMParser();
      const virtualDoc = parser.parseFromString(pageHtmlForModal, 'text/html');
      const headingText = (virtualDoc.getElementsByClassName('js-modal-header')[0] as HTMLHeadingElement)?.innerText;
      const modalBody = virtualDoc.getElementsByClassName('js-modal-body')[0];

      // Check if both headingText and modalBody are empty
      if (!headingText && !modalBody) {
        setModalContent(
          'Error: No Content Available',
          'The content was retrieved, but there’s nothing available to display. Please check the content at the provided URL and try again later.'
        );
        return;
      }

      // Set the appropriate content if both headingText and modalBody are available
      setModalContent(headingText, modalBody);
    } catch (error) {
      // Handle errors here
      console.error('Error fetching terms:', error);
      setModalContent('Error', 'Our server is being stubborn. Please try again later.');
    }
  };

  const sanitizedNode = DOMPurify.sanitize(modalBody, { RETURN_DOM: true }) as HTMLElement; // RETURN_DOM to resolve security issue
  const fragmentString = sanitizedNode.innerHTML; // Get the HTML content of the container

  const onModalClose = () => {
    setConsentModalInfo(prevState => ({
      ...prevState,
      showModal: false,
    }));

    // Return focus to the element that triggered the modal
    if (activeAnchorElementBeforeModalOpen.current) {
      activeAnchorElementBeforeModalOpen.current.focus();
    }
  };

  const onCheckboxToggle = (_selectedConsentId: string, selectedCheckboxId: string) => {
    const updatedConsentsState = toggleSelectedConsentCheckbox({ consentState, selectedCheckboxId });
    console.log('updatedConsentsState ', updatedConsentsState);
    onConsentChange({ updatedConsentsState });
  };

  // Usage for header and checkbox
  const onLinkClickInHeader = (e: React.MouseEvent<HTMLDivElement>) => {
    onLinkClickShowModal(e, '.js-header-list');
  };

  const onLinkClickInCheckbox = (e: React.MouseEvent<HTMLDivElement>) => {
    onLinkClickShowModal(e, '.js-checkbox-label');
  };

  const onLinkClickShowModal = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, containerClass: string) => {
    const target = e.target as HTMLElement;

    // Check if the clicked target is an anchor tag inside the specified container
    if (target.closest(containerClass) && target.tagName === 'A' && target.getAttribute('data-is-modal') === 'true') {
      e.preventDefault(); // Prevent default anchor behavior
      // Open the modal with the href of the link
      onModalOpen(e, target.getAttribute('href') as string);
    }
  };

  return (
    <StyledConsentsList data-testid="legal-ConsentList" data-component-name="o-legal-ConsentList">
      {consentState.list.map(consentData => (
        <ConsentsListItem
          key={`consentListItem-${consentData.id}`}
          consentData={consentData}
          showTopDivider={consentData.showTopDivider}
          showBottomDivider={consentData.showBottomDivider}
          consentSectionType={consentData.sectionType}
          validateErrors={validateErrors}
          onModalOpen={onModalOpen}
          onCheckboxToggle={onCheckboxToggle}
          onLinkClickInHeader={onLinkClickInHeader}
          onLinkClickInCheckbox={onLinkClickInCheckbox}
        />
      ))}
      {showModal && (
        <Modal
          role="dialog"
          labelledBy="termsModalLabel"
          show={showModal}
          popupOpenState={showModal}
          handleBlur={true}
          disableScrollOnBody={true}
          setPopupOpenState={onModalClose}
          onClose={onModalClose}
        >
          <Modal.Header
            labelText={modalHeader}
            customHeadingClass="t-subtitle-xl"
            closeBtnAriaLabel="close-button"
            popupHeaderOnCLoseFunc={onModalClose}
          />
          <Modal.Body className="p-0">
            {errorMessage ? (
              <Messages messageType="error-sev1" className={clsx('p-5')} messageText={errorMessage} />
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: fragmentString,
                }}
              ></div>
            )}
          </Modal.Body>
        </Modal>
      )}
    </StyledConsentsList>
  );
};
